import { useEffect, useId, useRef, useState } from "react";
import { Search as SearchIcon, Tag, Layers, Wrench, Package } from "lucide-react";
import { api } from "@/lib/api";
import { foldText } from "@/constants/searchKeywords";
import { useLang } from "@/context/LangContext";
import { categoryLabel, taxonomyLabel } from "@/lib/categoryLabel";

const TYPE_ICON = {
  equipment: Package,
  brand: Tag,
  model: Tag,
  category: Layers,
  subcategory: Layers,
  sector: Layers,
  offer: Wrench,
  listing: Package,
};

const TYPE_LABEL_KEY = {
  equipment: "search.suggest_equipment",
  brand: "search.suggest_brands",
  model: "search.suggest_models",
  category: "search.suggest_categories",
  subcategory: "search.suggest_categories",
  sector: "search.suggest_sectors",
  offer: "search.suggest_offers",
  listing: "search.suggest_listings",
};

function HighlightMatch({ text, query }) {
  if (!query?.trim()) return <>{text}</>;
  const foldedText = foldText(text);
  const foldedQuery = foldText(query);
  const idx = foldedText.indexOf(foldedQuery);
  if (idx < 0) return <>{text}</>;

  // Map folded indices back to original string indices (approx for accents)
  let oStart = 0;
  let fPos = 0;
  while (fPos < idx && oStart < text.length) {
    const ch = text[oStart];
    const folded = foldText(ch);
    fPos += folded.length || 1;
    oStart += 1;
  }
  let oEnd = oStart;
  let matched = 0;
  while (matched < foldedQuery.length && oEnd < text.length) {
    const ch = text[oEnd];
    const folded = foldText(ch);
    matched += folded.length || 1;
    oEnd += 1;
  }

  return (
    <>
      {text.slice(0, oStart)}
      <mark className="bg-matpro-green-light text-matpro-anthracite font-bold rounded-sm px-0.5">
        {text.slice(oStart, oEnd)}
      </mark>
      {text.slice(oEnd)}
    </>
  );
}

/**
 * Smart search input with autocomplete dropdown.
 * Suggestions from /search/suggestions (DB + seed keywords).
 */
export function SearchAutocomplete({
  value,
  onChange,
  onSubmit,
  onSelectSuggestion,
  placeholder,
  ariaLabel,
  inputId: inputIdProp,
  inputClassName = "",
  wrapperClassName = "",
  testId = "search-autocomplete",
  autoFocus = false,
  showIcon = true,
  debounceMs = 150,
}) {
  const { t, lang } = useLang();
  const inputId = inputIdProp || `${testId}-input`;
  const listId = useId();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const reqRef = useRef(0);

  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 1) {
      setItems([]);
      setActive(-1);
      return undefined;
    }
    const reqId = ++reqRef.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/search/suggestions", { params: { q, limit: 12 } });
        if (reqId !== reqRef.current) return;
        setItems(Array.isArray(data.items) ? data.items : []);
        setActive(-1);
        setOpen(true);
      } catch {
        if (reqId !== reqRef.current) return;
        setItems([]);
      } finally {
        if (reqId === reqRef.current) setLoading(false);
      }
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [value, debounceMs]);

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setActive(-1);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (item) => {
    onSelectSuggestion?.(item);
    if (!onSelectSuggestion) {
      onChange?.(item.label || item.value || "");
    }
    setOpen(false);
    setActive(-1);
  };

  const submit = () => {
    setOpen(false);
    onSubmit?.(value);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      setActive(-1);
      return;
    }
    if (e.key === "ArrowDown") {
      if (!open && items.length) setOpen(true);
      e.preventDefault();
      setActive((i) => Math.min((i < 0 ? -1 : i) + 1, items.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, -1));
      return;
    }
    if (e.key === "Enter") {
      if (open && active >= 0 && items[active]) {
        e.preventDefault();
        pick(items[active]);
        return;
      }
      e.preventDefault();
      submit();
    }
  };

  const showList = open && (value || "").trim().length >= 1 && (items.length > 0 || loading);

  // Group for section headers
  const groups = [];
  let lastType = null;
  items.forEach((item, index) => {
    if (item.type !== lastType) {
      groups.push({ type: item.type, start: index });
      lastType = item.type;
    }
  });

  return (
    <div ref={rootRef} className={`relative ${wrapperClassName}`} data-testid={testId}>
      {showIcon && (
        <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-zinc-400" aria-hidden />
      )}
      <input
        id={inputId}
        type="search"
        role="combobox"
        aria-label={ariaLabel || placeholder}
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${listId}-opt-${active}` : undefined}
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder}
        data-testid={`${testId}-input`}
        className={[
          showIcon ? "pl-10" : "pl-4",
          "matpass-search-input h-12 w-full rounded-xl border border-matpro-border bg-white pr-4 text-sm text-matpro-anthracite shadow-sm outline-none placeholder:text-zinc-400",
          inputClassName,
        ].join(" ")}
        onChange={(e) => {
          onChange?.(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          if ((value || "").trim().length >= 1) setOpen(true);
        }}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />

      {showList && (
        <div
          id={listId}
          role="listbox"
          className="absolute z-50 mt-1.5 max-h-80 w-full overflow-auto rounded-xl border border-matpro-border bg-white py-1.5 shadow-[0_12px_40px_rgba(24,24,27,0.12)]"
          data-testid={`${testId}-list`}
        >
          {loading && items.length === 0 && (
            <div className="px-4 py-3 text-sm text-matpro-anthracite-muted">{t("search.suggest_loading")}</div>
          )}
          {items.map((item, index) => {
            const Icon = TYPE_ICON[item.type] || Package;
            const showHeader = groups.some((g) => g.start === index);
            const itemLabel =
              item.type === "category" || item.action === "category"
                ? categoryLabel(item.value, t, item.label)
                : item.type === "subcategory" || item.action === "subcategory"
                  ? taxonomyLabel(item.value || item.label, lang, item.label)
                  : item.label;
            return (
              <div key={`${item.type}-${item.value}-${index}`}>
                {showHeader && (
                  <div className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.16em] text-matpro-anthracite-muted">
                    {t(TYPE_LABEL_KEY[item.type] || "search.suggest_equipment")}
                  </div>
                )}
                <button
                  type="button"
                  id={`${listId}-opt-${index}`}
                  role="option"
                  aria-selected={active === index}
                  onMouseEnter={() => setActive(index)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(item)}
                  className={[
                    "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors",
                    active === index ? "bg-matpro-green-light" : "hover:bg-matpro-bg",
                  ].join(" ")}
                >
                  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-matpro-bg text-matpro-green">
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-matpro-anthracite">
                    <HighlightMatch text={itemLabel} query={value} />
                  </span>
                  {item.count != null && (
                    <span className="shrink-0 text-[11px] text-matpro-anthracite-muted">
                      {item.count}
                    </span>
                  )}
                </button>
              </div>
            );
          })}
          {!loading && items.length === 0 && (
            <div className="px-4 py-3 text-sm text-matpro-anthracite-muted">{t("search.suggest_empty")}</div>
          )}
        </div>
      )}
    </div>
  );
}
